// frontend/src/components/parent/CourseProgressSection.tsx

import { useState } from 'react';
import { CourseProgressSummary, CourseLesson } from '../../types/parent.types';
import { parentService } from '../../services/parent.service';
import { IconCode, IconBrain, IconBook, IconCheckCircle, IconClock, IconX } from './ParentIcons';

const CATEGORY_ICON: Record<string, React.ReactNode> = {
  python: <IconCode size={18} />,
  game: <IconBook size={18} />,
  ai: <IconBrain size={18} />,
  web: <IconCode size={18} />,
  scratch: <IconBrain size={18} />,
};

const STATUS_LABEL: Record<string, { label: string; color: string; bg: string }> = {
  completed: { label: 'Tamamlandı', color: '#10C27A', bg: 'rgba(16,194,122,0.1)' },
  in_progress: { label: 'Devam Ediyor', color: '#00AEEF', bg: 'rgba(0,174,239,0.1)' },
  not_started: { label: 'Başlamadı', color: '#94A3B8', bg: 'rgba(148,163,184,0.1)' },
};

interface CourseProgressSectionProps {
  title: string;
  items: CourseProgressSummary[];
  emptyText: string;
  variant: 'strength' | 'growth';
  childId: string;
}

export function CourseProgressSection({ title, items, emptyText, variant, childId }: CourseProgressSectionProps) {
  const [selectedCourse, setSelectedCourse] = useState<{ id: string; title: string } | null>(null);
  const [lessons, setLessons] = useState<CourseLesson[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const barColor = variant === 'strength' ? '#10C27A' : '#F59E0B';
  const iconBg = variant === 'strength' ? 'rgba(16,194,122,0.1)' : 'rgba(245,158,11,0.1)';
  const iconColor = variant === 'strength' ? '#10C27A' : '#F59E0B';

  const handleCourseClick = async (courseId: string, courseTitle: string) => {
    setSelectedCourse({ id: courseId, title: courseTitle });
    setLoading(true);
    setError(null);
    try {
      const data = await parentService.getCourseLessons(childId, courseId);
      setLessons(data.lessons);
    } catch (err) {
      setError('Ders detayları yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setSelectedCourse(null);
    setLessons([]);
  };

  return (
    <div className="pcd-section-card">
      <div className="pcd-section-header">
        <div className="pcd-section-indicator" style={{ background: barColor }} />
        <h3 className="pcd-section-title">{title}</h3>
      </div>

      {items.length === 0 ? (
        <p className="pcd-empty-text">{emptyText}</p>
      ) : (
        <div className="pcd-progress-list">
          {items.map((item) => {
            const statusInfo = STATUS_LABEL[item.status] ?? STATUS_LABEL.in_progress;
            return (
              <div
                className="pcd-progress-row pcd-clickable-row"
                key={item.id || item.title}
                onClick={() => handleCourseClick(item.id, item.title)}
                style={{ cursor: 'pointer' }}
                title="Ders detaylarını görmek için tıklayın"
              >
                <div className="pcd-progress-icon" style={{ background: iconBg, color: iconColor }}>
                  {CATEGORY_ICON[item.category] ?? <IconBook size={18} />}
                </div>
                <div className="pcd-progress-info">
                  <div className="pcd-progress-name">{item.title}</div>
                  <div className="pcd-progress-track">
                    <div className="pcd-progress-bar-wrap">
                      <div
                        className="pcd-progress-bar-fill"
                        style={{ width: `${item.progressPercent}%`, background: barColor }}
                      />
                    </div>
                    <span className="pcd-progress-pct">{item.progressPercent}%</span>
                  </div>
                </div>
                <span
                  className="pcd-status-badge"
                  style={{ color: statusInfo.color, background: statusInfo.bg }}
                >
                  {statusInfo.label}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* ─── DERS DETAY MODALI ──────────────────────────────── */}
      {selectedCourse && (
        <div className="pcd-modal-overlay" onClick={handleCloseModal}>
          <div className="pcd-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="pcd-modal-header">
              <div>
                <h4 className="pcd-modal-title">{selectedCourse.title}</h4>
                <p className="pcd-modal-subtitle">Ders Detayları ve İlerleme Raporu</p>
              </div>
              <button type="button" className="pcd-modal-close" onClick={handleCloseModal}>
                <IconX size={20} />
              </button>
            </div>

            <div className="pcd-modal-body">
              {loading && (
                <div className="pcd-modal-loading">
                  <div className="pcd-spinner" />
                  <p>Dersler yükleniyor...</p>
                </div>
              )}

              {error && (
                <div className="pcd-modal-error">
                  <IconX size={24} style={{ color: '#EF4444' }} />
                  <p>{error}</p>
                </div>
              )}

              {!loading && !error && lessons.length === 0 && (
                <p className="pcd-empty-text">Bu kursa ait herhangi bir ders bulunamadı.</p>
              )}

              {!loading && !error && lessons.length > 0 && (
                <div className="pcd-modal-lessons-list">
                  {lessons.map((lesson) => (
                    <div
                      key={lesson.id}
                      className={`pcd-modal-lesson-row ${lesson.isCompleted ? 'completed' : ''}`}
                    >
                      <div className="pcd-lesson-status-icon">
                        {lesson.isCompleted ? (
                          <IconCheckCircle size={20} style={{ color: '#10C27A' }} />
                        ) : (
                          <IconClock size={20} style={{ color: '#94A3B8' }} />
                        )}
                      </div>
                      <div className="pcd-lesson-details">
                        <div className="pcd-lesson-title">
                          <span className="pcd-lesson-order">{lesson.orderIndex + 1}.</span>
                          {lesson.title}
                        </div>
                        {lesson.isCompleted && lesson.completedAt && (
                          <div className="pcd-lesson-completed-date">
                            Tamamlanma Tarihi: {new Date(lesson.completedAt).toLocaleDateString('tr-TR', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </div>
                        )}
                      </div>
                      <div className="pcd-lesson-reward">
                        <span className="pcd-reward-badge">+{lesson.xpReward} XP</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
